import type { GetSchemaResponse } from '@compass/client/types/schema';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { schemaService } from '@/services/api';
import { queryKeys } from '@/services/query.config';
import { useStore } from '@/store/store';
import type { FieldGroup } from '@/types/formSchema';
import { parseFieldGroups } from '@/utils/formSchema';

/**
 * Helper to safely parse field groups from schema response.
 * Extracted to reduce cyclomatic complexity of useSchema hook.
 */
function parseGroups(groups: unknown): FieldGroup[] {
  if (groups && Array.isArray(groups)) {
    return parseFieldGroups(groups);
  }
  return [];
}

/**
 * Helper to determine if schema data is available.
 * Extracted to reduce cyclomatic complexity of useSchema hook.
 */
function hasSchemaData(data: GetSchemaResponse | undefined): boolean {
  return Boolean(data && (data.input || data.output));
}

export interface UseSchemaResult {
  /** Full schema response from the API */
  schema: GetSchemaResponse | undefined;
  /** Parsed input field groups */
  inputGroups: FieldGroup[];
  /** Parsed output field groups */
  outputGroups: FieldGroup[];
  /** Status configuration from schema */
  statusConfig: Record<string, any> | undefined;
  /** Schema version */
  version: string | undefined;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  isError: boolean;
  /** Error object if any */
  error: Error | null;
  /** Whether schema data is available */
  hasSchema: boolean;
}

/**
 * Hook to fetch and parse workflow UI schema from the schema service.
 *
 * @param workflowId - The workflow ID to fetch schema for (null/undefined to skip)
 * @param schemaVersion - The record's nomos schema major (case output_data.schema_version); omit for the workflow's current schema
 * @returns UseSchemaResult with parsed schema data and loading states
 *
 * @example
 * const { inputGroups, outputGroups, statusConfig, isLoading } = useSchema(selectedWorkflow?.id);
 */
export function useSchema(
  workflowId: string | null | undefined,
  schemaVersion?: string,
): UseSchemaResult {
  const organizationId = useStore(state => state.selectedOrganizationId);
  const programId = useStore(state => state.selectedProgramId);

  const query = useQuery({
    queryKey: queryKeys.schema.byWorkflow(organizationId!, programId!, workflowId!, schemaVersion),
    queryFn: () =>
      schemaService.getSchema({
        organization_id: organizationId!,
        program_id: programId!,
        workflow_id: workflowId!,
        ...(schemaVersion ? { schema_version: schemaVersion } : {}),
      }),
    enabled: !!organizationId && !!programId && !!workflowId,
    staleTime: 30 * 60 * 1000, // 30 minutes - schemas change infrequently
    gcTime: 60 * 60 * 1000, // 1 hour
  });

  const inputGroups = useMemo<FieldGroup[]>(
    () => parseGroups(query.data?.input?.groups),
    [query.data?.input?.groups],
  );

  // Parse output groups from schema response (memoized to ensure stable references)
  const outputGroups = useMemo<FieldGroup[]>(
    () => parseGroups(query.data?.output?.groups),
    [query.data?.output?.groups],
  );

  return {
    schema: query.data,
    inputGroups,
    outputGroups,
    statusConfig: query.data?.status,
    version: query.data?.version,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    hasSchema: hasSchemaData(query.data),
  };
}
